/**
 * One send, from `POST /chat` to its end: maps what `streamChat` reports
 * (SSE events, resolving, rejecting) and Stop to `chatReducer` actions, with
 * `delta` text batched per frame (plan §6.3). Framework-free (copied to
 * next-mapit); `hooks/useAiChat.js` only wires it to fetch and React.
 *
 * Exactly one terminal action per turn — `done`, `streamError`,
 * `streamEndedWithoutDone`, `apiError` or `aborted` — always after the
 * buffered text is flushed. Anything after it is ignored: an abort can't also
 * fail the answer, and a late delta can't land in the next turn.
 */

import {
  aborted,
  apiError,
  deltaBatch,
  done,
  meta,
  streamEndedWithoutDone,
  streamError,
} from "./chatReducer";
import { createDeltaBatcher } from "./deltaBatcher";
import { isChatApiError } from "./errors";
import { ERROR_CODES, SSE_EVENTS } from "./protocol";

/** A rejection that isn't a ChatApiError (a bug of ours): generic failure. */
const UNKNOWN_FAILURE = Object.freeze({ code: null });

/**
 * @typedef {Object} ChatTurnOptions
 * @property {(action: { type: string }) => void} dispatch `chatReducer` dispatch.
 * @property {import("./deltaBatcher").FrameScheduler["schedule"]} schedule
 * @property {import("./deltaBatcher").FrameScheduler["cancel"]} cancel
 * @property {string | null} [conversationId] Where the message goes; null for
 *   a draft (`meta` names the new conversation).
 * @property {(info: { conversationId: string | null, isNew: boolean }) => void} [onMeta]
 *   After `meta` was dispatched.
 * @property {(info: { conversationId: string | null }) => void} [onDone]
 *   After `done` was dispatched (invalidate the caches).
 * @property {() => void} [onAbandon] The client ended the turn while the
 *   response is still open (a malformed frame): abort the request, so the
 *   server stops generating and the next send isn't refused with 854.
 */

/**
 * @typedef {Object} ChatTurn
 * @property {(event: import("./sseParser").SseEvent) => void} handleEvent `streamChat` `onEvent`.
 * @property {() => void} finish `streamChat` resolved: without `done`/`error` the answer failed.
 * @property {(error: unknown, now: number) => void} fail `streamChat` rejected.
 * @property {() => void} stop Stop: the answer is `aborted` (abort the request too).
 * @property {() => void} dispose Unmount: drops buffered text, dispatches nothing more.
 * @property {() => boolean} isEnded Whether the terminal action happened (or `dispose`).
 */

/**
 * @param {ChatTurnOptions} options
 * @returns {ChatTurn}
 */
export function createChatTurn({
  dispatch,
  schedule,
  cancel,
  conversationId = null,
  onMeta,
  onDone,
  onAbandon,
}) {
  let ended = false;
  let currentConversationId = conversationId;
  const batcher = createDeltaBatcher({
    schedule,
    cancel,
    onFlush: (text) => dispatch(deltaBatch(text)),
  });

  /** Flushes the text that arrived so far, then ends the turn with `action`. */
  const end = (action) => {
    if (ended) return false;
    ended = true;
    batcher.flushNow();
    batcher.dispose();
    dispatch(action);
    return true;
  };

  const handleEvent = (event) => {
    if (ended) return;
    const data = event?.data;
    switch (event?.event) {
      case SSE_EVENTS.META: {
        batcher.flushNow();
        currentConversationId = data?.conversation?.id ?? currentConversationId;
        dispatch(meta(data));
        onMeta?.({
          conversationId: currentConversationId,
          isNew: data?.conversation?.isNew === true,
        });
        break;
      }
      case SSE_EVENTS.DELTA:
        batcher.push(data?.t);
        break;
      case SSE_EVENTS.DONE:
        end(done(data));
        onDone?.({ conversationId: currentConversationId });
        break;
      case SSE_EVENTS.ERROR:
        // A server `error` ends its response; the client's `parse` doesn't
        end(streamError(data));
        if (data?.code === ERROR_CODES.PARSE) onAbandon?.();
        break;
      default:
      // Frames without `event:` arrive as "message"; unknown events too
    }
  };

  return {
    handleEvent,
    finish: () => {
      end(streamEndedWithoutDone());
    },
    fail: (error, now) => {
      end(apiError(isChatApiError(error) ? error : UNKNOWN_FAILURE, now));
    },
    stop: () => {
      end(aborted());
    },
    dispose: () => {
      ended = true;
      batcher.dispose();
    },
    isEnded: () => ended,
  };
}
